import { Form, message, Typography } from 'antd';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';

import { CheckBoxField, FormItem, SubmitButton, TextField } from 'componets/form';
import { RiskNotice } from '../../../RiskNotice';
import { PrimaryMarketProjectPromoCodeModal } from '../PrimaryMarketProjectPromoCodeModal';

import {
	getPrimaryMarketItem,
	getPrimaryMarketItemAgreement,
	investInPrimaryMarketItem,
} from 'features/primaryMarket/actions';
import { getBalance } from 'features/overview/actions';

import { FieldData } from 'models';

import { useAppDispatch, useAppSelector } from 'hooks';

import { Rules, RequestStatus } from 'utils/constants';
import Utils from 'utils/utils';

import { selectIsAdmin, selectOverview } from 'selectors';

import { ReactComponent as SuccessIcon } from 'assets/icons/success-verify.svg';
import { PleaseNotePopup } from './PleaseNotePopup';
import { Coupon } from '../../../../../../models/Coupon';

export const PrimaryMarketProjectInvestForm = () => {
	const overview = useAppSelector(selectOverview);
	const isAdmin = useAppSelector(selectIsAdmin);

	const { item: project, status } = useAppSelector((state) => state.primaryMarket);

	const [isOpen, setIsOpen] = useState(false);
	const [pleaseNotePopup, setPleaseNotePopup] = useState(false);
	const [isPromoCodeOpen, setIsPromoCodeOpen] = useState(false);
	const [coupon, setCoupon] = useState<Coupon>();
	const [values, setValues] = useState({} as { amount: string });
	const inputRef = useRef(null);

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);

	const { projectId } = useParams();

	const amount = Form.useWatch('amount', form);

	const { t } = useTranslation();

	const dispatch = useAppDispatch();

	const getRange = (amount: number, { ranges }: Coupon) => {
		const payload = ranges.map((range) => {
			if (amount >= Number(range.from)) {
				return !(range?.to && amount > Number(range.to));
			}
			return false;
		});
		return payload.includes(true);
	};

	const onFinish = async (body: { amount: string }) => {
		if (coupon) {
			const amount = Number(body.amount);
			if (!getRange(amount, coupon)) {
				setPleaseNotePopup(true);
				return;
			}
		}
		setIsOpen(true);
		setValues(body);
	};

	const onFinishResult = async () => {
		return dispatch(
			investInPrimaryMarketItem({ body: { ...values, coupon: coupon?.code }, id: project.loanId }),
		)
			.unwrap()
			.then(() => {
				if (projectId !== null) {
					dispatch(getPrimaryMarketItem(projectId || ''));
					dispatch(getBalance());
				}
				form.resetFields();
				setCoupon(undefined);
			})
			.catch((errors) => {
				const messageText = t(errors.message);
				try {
					const minAmountMatch = messageText.match(/The minimal amount is (\d+)/);
					if (!!minAmountMatch) {
						const minAmount = +minAmountMatch[1];
						setFields([
							{
								name: ['amount'],
								errors: [t('errorMinimumAmountText', { amount: minAmount })],
							},
						]);
						return;
					}
				} catch (err) {
					console.warn(err);
				}
				setFields([{ name: ['amount'], errors: [messageText] }]);
				if (errors.message === 'Undefined risk tolerance') {
					message.error(messageText);
				}
			})
			.finally(() => {
				setIsOpen(false);
			});
	};

	const onDownload = () => {
		if (!amount) {
			form.setFields([{ name: 'amount', errors: [t('errorEmptyText')] }]);
			return;
		}

		dispatch(
			getPrimaryMarketItemAgreement({
				id: project.loanId,
				amount,
			}),
		)
			.unwrap()
			.catch((errors) => {
				const messageText = t(errors?.errors?.amount?.[0]);
				try {
					const minAmountMatch = messageText.match(/Amount must be no less than (\d+)/);
					if (!!minAmountMatch) {
						const minAmount = +minAmountMatch[1];
						setFields([
							{
								name: ['amount'],
								errors: [t('errorMinimumAmountText', { amount: minAmount })],
							},
						]);
						return;
					}
				} catch (err) {
					console.warn(err);
				}

				setFields([{ name: ['amount'], errors: [messageText] }]);

				if (errors.message === 'Undefined risk tolerance') {
					message.error(messageText);
				}
			});
	};

	return (
		<Form
			className="primaryItem__form"
			onFinish={onFinish}
			onFieldsChange={setFields}
			fields={fields}
			form={form}
		>
			<div className="primaryItem__insideForm">
				<FormItem
					validateTrigger="onSubmit"
					name="amount"
					className="primaryItem__amount"
					rules={[
						Rules.REQUIRED,
						Rules.LOW_BALANCE(+overview.availableMoney),
						Rules.VERY_BIG_AMOUNT(+project.availableToInvest),
					]}
					normalize={(v) => Utils.transformToFixedNumber(v)}
				>
					<TextField
						ref={inputRef}
						label={t('enterInvestmentAmountText')}
						onChange={() => {
							if (form.getFieldError('amount').length) {
								setFields([{ name: ['amount'], errors: [] }]);
							}
						}}
					/>
				</FormItem>

				<div className="primaryItem__promocode">
					{coupon?.code ? (
						<div className="primaryItem__green">
							<SuccessIcon /> {t('appliedCouponText')}
						</div>
					) : (
						<button type="button" onClick={() => setIsPromoCodeOpen(true)}>
							{t('enterPromoCodeText')}
						</button>
					)}
				</div>
			</div>

			<div className="primaryItem__inputInfo">
				<Typography.Text type="secondary">{t('yourAvailableFundsText')}:&nbsp;</Typography.Text>
				{Utils.moneyFormat(Number(overview.availableMoney))}
			</div>

			<div className="primaryItem__inputInfo">
				<Typography.Text type="secondary">{t('yourInvestmentProjectText')}:&nbsp;</Typography.Text>
				{Utils.moneyFormat(Number(project.amountOfInvestment))}
			</div>

			<FormItem
				name="confirm"
				className="primaryItem__checkbox primaryItem__insideForm checkbox"
				rules={[Rules.REQUIRED_CHECKBOX]}
			>
				<CheckBoxField
					text={
						<Trans
							i18nKey="confirmAgreementText"
							components={{
								agreement_link: (
									<button className="primaryItem__linkButton" type="button" onClick={onDownload} />
								),
							}}
						/>
					}
				/>
			</FormItem>

			<SubmitButton
				text={t('investText')}
				className={cn('primaryItem__button', { 'ant-btn-grey': isAdmin })}
				loading={status === RequestStatus.PENDING}
			/>

			<RiskNotice
				loading={status === RequestStatus.PENDING}
				onFinish={onFinishResult}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
			/>

			<PleaseNotePopup
				isOpen={pleaseNotePopup}
				onClose={() => {
					form.resetFields();
					setPleaseNotePopup(false);
					setTimeout(() => {
						if (inputRef.current) {
							(inputRef.current as any).focus();
						}
					}, 0);
				}}
				onGotIt={() => {
					setCoupon(undefined);
					setPleaseNotePopup(false);
				}}
			/>

			<PrimaryMarketProjectPromoCodeModal
				isOpen={isPromoCodeOpen}
				onClose={() => setIsPromoCodeOpen(false)}
				onSetCoupon={(coupon) => {
					return new Promise((resolve) => {
						setCoupon(coupon);
						resolve();
					});
				}}
			/>
		</Form>
	);
};
